import { supabase } from '../lib/supabase';
import type { Point, GenerationMode } from '../types';
import {
    addWatermark,
    uploadToSupabase,
    cropImage,
    createInpaintedImage,
    compositeInpaintedImage
} from '../utils/imageProcessing';
import { constructVirtualControlsPrompt, planToFurnitureRulesPrompt, constructMaskingProtocol, standaloneViewGenerationPrompt } from '../config/corePrompts';
import {
    ApiError,
    AuthError,
    PaymentError,
    RateLimitError,
    ImageProcessingError,
    ConfigurationError,
} from '../errors';
import { withRetry } from '../utils/retry';
import { logError } from '../utils/errorLogger';

/**
 * Process Gemini API response and handle errors
 */
function processGeminiResponse(result: any, outputMode: string, originalImageSrc: string, points: Point[] | null, canvasDimensions: any, userId?: string): Promise<string> {
    if (result.candidates && result.candidates[0].content.parts) {
        for (const part of result.candidates[0].content.parts) {
            if (part.inlineData) {
                const generatedImageSrc = `data:${part.inlineData.mimeType};base64,${part.inlineData.data}`;

                let finalImageSrc: string;
                if (outputMode === 'standalone' || !points || points.length === 0) {
                    finalImageSrc = generatedImageSrc;
                } else {
                    return compositeInpaintedImage(originalImageSrc, generatedImageSrc, points, canvasDimensions)
                        .then(addWatermark)
                        .then(watermarked => {
                            if (userId) {
                                uploadToSupabase(watermarked, userId).catch(console.error);
                            }
                            return watermarked;
                        });
                }

                return addWatermark(finalImageSrc).then(watermarked => {
                    if (userId) {
                        uploadToSupabase(watermarked, userId).catch(console.error);
                    }
                    return watermarked;
                });
            }
        }
    }

    const textResponse = result.text || (result.candidates && result.candidates[0].content.parts[0].text);
    throw new ImageProcessingError(`API did not return an image. Response: ${textResponse || 'No text response found.'}`);
}

/**
 * Generates an elevation from a floor plan.
 */
export const callGeminiPlanToElevation = async (
    groundFloor: string,
    typicalFloor: string | null,
    specs: { groundHeight: number; typicalHeight: number; floors: number },
    stylePrompt: string,
    userId: string
): Promise<string> => {
    const structureDesc = `The building has a ground floor of ${specs.groundHeight}m height and ${specs.floors} typical floors of ${specs.typicalHeight}m height each.`;
    const prompt = `Generate a photorealistic architectural elevation based on this floor plan. ${structureDesc} Style: ${stylePrompt}. High quality, detailed facade, realistic lighting.`;

    return callGeminiImageEditor(
        groundFloor,
        null,
        { width: 1024, height: 1024 },
        prompt,
        typicalFloor,
        'create',
        "blur, low quality, distorted, drawing, sketch",
        90,
        40,
        80,
        80,
        userId,
        'standalone'
    );
};

/**
 * Generates a furnished 3D floor plan from a 2D floor plan image.
 */
export const callGeminiPlanToFurniture = async (
    floorPlanImageBase64: string,
    stylePrompt: string,
    userId: string,
    colorLegend?: Record<string, string>,
    roomTypes?: string[]
): Promise<string> => {

    let legendBlock = '';
    if (colorLegend && Object.keys(colorLegend).length > 0) {
        const legendLines = Object.entries(colorLegend)
            .map(([hex, label]) => `  - Color ${hex} = ${label}`)
            .join('\n');
        legendBlock = planToFurnitureRulesPrompt.replace('  - Color #123456 = Placeholder', legendLines);
    }

    let roomManifest = '';
    if (roomTypes && roomTypes.length > 0) {
        roomManifest = `\nROOMS PRESENT IN THIS PLAN: ${roomTypes.join(', ')}.\n`;
    }

    const prompt = `You are an expert architectural interior visualization artist.

TASK: Convert this color-coded 2D architectural floor plan into a fully furnished, high-quality, top-down 3D rendered floor plan.
${legendBlock}${roomManifest}
STYLE: ${stylePrompt}

OUTPUT REQUIREMENTS:
- Photorealistic top-down 3D render (bird's-eye view).
- Keep ALL walls, room boundaries, and overall geometry EXACTLY as shown. Do NOT change any dimensions.
- Add appropriate, correctly-scaled loose furniture to each room based on its type.
- Use realistic materials, subtle shadows, and warm lighting.
- Maintain the same room layout proportions as the input plan.`;

    return callGeminiImageEditor(
        floorPlanImageBase64,
        null,
        { width: 1024, height: 1024 },
        prompt,
        null,
        'modify',
        "blur, low quality, distorted, sketch, changed walls, changed layout, missing rooms, bathroom fixtures in kitchen, kitchen appliances in bathroom, furniture outside room boundaries, wrong room type furniture",
        80,
        0,
        80,
        80,
        userId,
        'standalone'
    );
};

/**
 * Main Gemini image editor function with error handling and retry logic
 */
export async function callGeminiImageEditor(
    originalImageSrc: string,
    points: Point[] | null | undefined,
    canvasDimensions: { width: number; height: number },
    prompt: string,
    referenceImageSrcs: string[] | string | null | undefined,
    generationMode: GenerationMode,
    negativePrompt: string,
    aiStrength: number,
    resemblance: number,
    clarity: number,
    sharpness: number,
    userId?: string,
    outputMode: 'composite' | 'standalone' = 'composite'
): Promise<string> {

    let processedImageBase64: string;
    let mimeType: string;

    try {
        // Process input image
        if (outputMode === 'standalone' && points && points.length > 0) {
            const result = await cropImage(originalImageSrc, points, canvasDimensions);
            processedImageBase64 = result.croppedImageBase64;
            mimeType = result.mimeType;
        } else if (points && points.length > 0) {
            const result = await createInpaintedImage(originalImageSrc, points, canvasDimensions);
            processedImageBase64 = result.maskedImageBase64;
            mimeType = result.mimeType;
        } else {
            mimeType = originalImageSrc.substring(originalImageSrc.indexOf(':') + 1, originalImageSrc.indexOf(';'));
            processedImageBase64 = originalImageSrc.split(',')[1];
        }

        if (!processedImageBase64) {
            throw new ImageProcessingError("Failed to process input image.");
        }

        // Build prompts
        const virtualControlsPrompt = constructVirtualControlsPrompt(aiStrength, resemblance, clarity, sharpness);

        let generationInstructions = '';
        if (generationMode === 'modify') {
            generationInstructions = `Modify and enhance the existing content based on the user's request. You may update materials, lighting, textures, and style significantly if the prompt requests it. Maintain the underlying perspective and main structural massing unless asked to change them.`;
        } else {
            generationInstructions = `Generate new content for the selected area based EXCLUSIVELY on the user's request. You may fill the area with the requested materials/objects, but do NOT add unrelated surrounding context or buildings outside the scope of the prompt. Keep the design clean and intentional.`;
        }

        const maskingProtocol = constructMaskingProtocol(generationMode, outputMode, generationInstructions, prompt);

        let fullPrompt = `${prompt}

${virtualControlsPrompt}

${maskingProtocol}`;

        if (outputMode === 'standalone') {
            fullPrompt += standaloneViewGenerationPrompt;
        }

        // Build request parts
        const parts: any[] = [
            {
                inlineData: {
                    data: processedImageBase64,
                    mimeType: mimeType,
                },
            },
        ];

        if (referenceImageSrcs) {
            const refs = Array.isArray(referenceImageSrcs) ? referenceImageSrcs : [referenceImageSrcs];
            refs.forEach(refSrc => {
                if (!refSrc) return;
                const refMimeType = refSrc.substring(refSrc.indexOf(':') + 1, refSrc.indexOf(';'));
                const refBase64Data = refSrc.split(',')[1];
                if (refBase64Data) {
                    parts.push({
                        inlineData: {
                            data: refBase64Data,
                            mimeType: refMimeType,
                        },
                    });
                }
            });
        }

        if (negativePrompt && negativePrompt.trim()) {
            fullPrompt += `\n\n[--NEGATIVE PROMPT (DO NOT GENERATE)--]\n${negativePrompt.trim()}`;
        }

        parts.push({ text: fullPrompt });

        // Call Edge Function with retry logic
        const { data: result, error } = await withRetry(
            async () => {
                const response = await supabase.functions.invoke('gemini-proxy', {
                    body: {
                        model: 'gemini-2.5-flash-image',
                        contents: { parts },
                        config: {
                            responseModalities: ["IMAGE", "TEXT"],
                        }
                    }
                });

                if (response.error) {
                    throw new Error(response.error.message || 'Edge Function error');
                }

                return response;
            },
            {
                maxRetries: 2,
                onRetry: (error, attempt) => {
                    logError(error, {
                        userId,
                        metadata: { attempt, operation: 'gemini-proxy-call' },
                    });
                },
            }
        );

        if (error) {
            // Handle specific error types
            const errorMessage = error.message.toLowerCase();
            
            if (errorMessage.includes('authorization') || errorMessage.includes('auth')) {
                throw new AuthError('Authentication failed. Please log in again.');
            }
            if (errorMessage.includes('credit') || errorMessage.includes('insufficient')) {
                throw new PaymentError('Insufficient credits. Please add credits to continue.');
            }
            if (errorMessage.includes('rate limit')) {
                throw new RateLimitError('Too many requests. Please wait a moment.', 60);
            }
            if (errorMessage.includes('edge function')) {
                throw new ApiError('Service temporarily unavailable. Please try again.', 503);
            }
            
            throw new ApiError(error.message || 'Failed to generate image');
        }

        if (result.error) {
            throw new ApiError(`Gemini API Error: ${result.error}`);
        }

        // Process response
        return processGeminiResponse(result, outputMode, originalImageSrc, points, canvasDimensions, userId);

    } catch (error) {
        // Re-throw known errors
        if (error instanceof ImageProcessingError || error instanceof AuthError || error instanceof PaymentError || error instanceof RateLimitError || error instanceof ApiError) {
            throw error;
        }
        
        // Wrap unknown errors
        logError(error as Error, { userId, metadata: { operation: 'callGeminiImageEditor' } });
        throw new ImageProcessingError(
            error instanceof Error ? error.message : 'Failed to process image'
        );
    }
}

/**
 * Auto-generates a prompt from an image
 */
export async function callGeminiAutoPrompt(imageSrc: string): Promise<string> {
    const mimeType = imageSrc.substring(imageSrc.indexOf(':') + 1, imageSrc.indexOf(';'));
    const base64ImageData = imageSrc.split(',')[1];

    if (!base64ImageData) {
        throw new ConfigurationError("Invalid image source for auto-prompting.");
    }

    const prompt = "Analyze the provided image and generate a detailed, descriptive text prompt that, if used independently, would create a new, highly realistic version of this image. The prompt should be a photorealistic description of the scene, including subjects, setting, lighting, colors, and overall mood. Do not include any introductory phrases like 'This is an image of...'. Just provide the prompt itself.";

    try {
        const { data: result, error } = await supabase.functions.invoke('gemini-proxy', {
            body: {
                model: 'gemini-2.5-flash',
                contents: {
                    parts: [
                        {
                            inlineData: {
                                data: base64ImageData,
                                mimeType: mimeType,
                            },
                        },
                        {
                            text: prompt,
                        },
                    ]
                }
            }
        });

        if (error) throw error;
        if (result?.error) throw new Error(result.error);

        return result?.text || (result?.candidates && result.candidates[0]?.content?.parts?.[0]?.text) || "";
    } catch (error) {
        logError(error as Error, { metadata: { operation: 'callGeminiAutoPrompt' } });
        throw new ApiError('Failed to generate prompt from image');
    }
}

/**
 * Auto-generates a negative prompt from an image
 */
export async function callGeminiAutoNegativePrompt(imageSrc: string): Promise<string> {
    const mimeType = imageSrc.substring(imageSrc.indexOf(':') + 1, imageSrc.indexOf(';'));
    const base64ImageData = imageSrc.split(',')[1];

    if (!base64ImageData) {
        throw new ConfigurationError("Invalid image source for auto-negative-prompting.");
    }

    const prompt = "Analyze the provided image. Identify the core, unchangeable elements. Generate a comma-separated list of these elements for a negative prompt, prefixed with phrases like 'unchanged', 'preserve', or 'do not alter'. The goal is to prevent the AI from removing or significantly changing these key features. For example: 'unchanged woman in red dress, preserve the background mountains, do not alter the cat's pose'.";

    try {
        const { data: result, error } = await supabase.functions.invoke('gemini-proxy', {
            body: {
                model: 'gemini-2.5-flash',
                contents: {
                    parts: [
                        {
                            inlineData: {
                                data: base64ImageData,
                                mimeType: mimeType,
                            },
                        },
                        {
                            text: prompt,
                        },
                    ]
                }
            }
        });

        if (error) throw error;
        if (result?.error) throw new Error(result.error);

        return result?.text || (result?.candidates && result.candidates[0]?.content?.parts?.[0]?.text) || "";
    } catch (error) {
        logError(error as Error, { metadata: { operation: 'callGeminiAutoNegativePrompt' } });
        throw new ApiError('Failed to generate negative prompt');
    }
}

/**
 * Generates text from a prompt
 */
export async function generateText(prompt: string): Promise<string> {
    try {
        const { data: result, error } = await supabase.functions.invoke('gemini-proxy', {
            body: {
                model: 'gemini-2.5-flash',
                contents: { parts: [{ text: prompt }] }
            }
        });

        if (error) throw error;
        if (result?.error) throw new Error(result.error);

        return result?.text || (result?.candidates && result.candidates[0]?.content?.parts?.[0]?.text) || "";
    } catch (error) {
        logError(error as Error, { metadata: { operation: 'generateText' } });
        throw new ApiError('Failed to generate text response');
    }
}
